import React from 'react';
import { Button, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useIntl } from 'react-intl';
import ActionDialogShell from './ActionDialogShell';
import PublicationSelection from '../components/PublicationSelection';


export default function SchedulePublishDialog({
    open,
    onClose,
    publishReasonExtra,
    setPublishReasonExtra,
    publishScheduleLocal,
    setPublishScheduleLocal,
    publishScheduleValid,
    isPending,
    isArticleBodyLoading,
    isError,
    errorText,
    onSchedule,
    // Multi-publication props
    targetMediaOptions,
    onTargetMediaChange,
    effectiveTargetMedia,
    effectiveMultiPublicationTargetMedias,
    availableMultiPublicationTargetMedias,
    canEditFields,
    setArrayField,
    editableFields,
    getTargetMediaLabel,
}) {
    const theme = useTheme();
    const intl = useIntl();
    const extraReasonInputRef = React.useRef(null);

    React.useEffect(() => {
        if (!open) return;
        const timer = window.setTimeout(() => {
            extraReasonInputRef.current?.focus();
        }, 0);
        return () => window.clearTimeout(timer);
    }, [open]);

    return (
        <ActionDialogShell
            open={open}
            onClose={onClose}
            title={intl.formatMessage({ id: 'newsQueue.dialog.schedule.title' })}
            headerBgColor={theme.palette.scheduled?.main}
            headerTextColor={theme.palette.scheduled?.contrastText}
            actions={
                <>
                    <Button disabled={isPending} onClick={onClose}>
                        {intl.formatMessage({ id: 'newsQueue.common.cancel' })}
                    </Button>
                    <Button
                        color="primary"
                        sx={{
                            bgcolor: theme.palette.scheduled?.main,
                            color: theme.palette.scheduled?.contrastText,
                            '&:hover': {
                                bgcolor: theme.palette.scheduled?.main,
                            },
                        }}
                        disabled={!publishScheduleValid || isPending || isArticleBodyLoading}
                        onClick={onSchedule}
                    >
                        {intl.formatMessage({ id: 'newsQueue.actions.schedule' })}
                    </Button>
                </>
            }
        >
            <PublicationSelection
                open={open}
                effectiveTargetMedia={effectiveTargetMedia}
                targetMediaOptions={targetMediaOptions}
                onTargetMediaChange={onTargetMediaChange}
                effectiveMultiPublicationTargetMedias={effectiveMultiPublicationTargetMedias}
                availableMultiPublicationTargetMedias={availableMultiPublicationTargetMedias}
                canEditFields={canEditFields}
                isPending={isPending || isArticleBodyLoading}
                setArrayField={setArrayField}
                editableFields={editableFields}
                getTargetMediaLabel={getTargetMediaLabel}
            />
            <TextField
                autoFocus
                inputRef={extraReasonInputRef}
                margin="dense"
                id="publish-extra-schedule"
                label={intl.formatMessage({ id: 'newsQueue.feedback.extraLabel' })}
                type="text"
                fullWidth
                multiline
                minRows={3}
                variant="outlined"
                value={publishReasonExtra}
                onChange={(e) => setPublishReasonExtra(e.target.value)}
                helperText={intl.formatMessage({ id: 'newsQueue.feedback.optionalHelper' })}
                sx={{ mt: 2 }}
            />

            <TextField
                margin="dense"
                id="publish-schedule"
                label={intl.formatMessage({ id: 'newsQueue.schedule.datetimeLabel' })}
                type="datetime-local"
                fullWidth
                variant="outlined"
                value={publishScheduleLocal}
                onChange={(e) => setPublishScheduleLocal(e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText={intl.formatMessage({ id: 'newsQueue.schedule.datetimeHelp' })}
            />

            <Typography variant="body2">
                {intl.formatMessage({ id: 'newsQueue.publish.confirmSchedule' })}
            </Typography>
            {isError && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    {String(errorText ?? '').trim() ||
                        intl.formatMessage({ id: 'newsQueue.schedule.error.generic' })}
                </Typography>
            )}
        </ActionDialogShell>
    );
}
